import {
  AddPersonAssignmentData,
  AddPersonAssignmentResponse,
  Address,
  AddressDetail,
  ApprovalData,
  ApproveUserResponse,
  AssignFriendMatchResponse,
  AssignmentDetailResponse,
  BanUserData,
  BanUserResponse,
  BatchAssignPeopleData,
  BatchAssignPeopleResponse,
  Contact,
  CreateAssignmentData,
  CreateAssignmentResponse,
  CreateContactData,
  CreateLinkData,
  CreateLinkResponse,
  CreateNoteData,
  CreatePostcardCampaignData,
  CreatePreApprovalData,
  CreateTagData,
  CreateTagDefinitionData,
  CreateTagDefinitionResponse,
  DashboardMetrics,
  DeleteAssignmentResponse,
  DeletePreApprovalResponse,
  DeleteTagDefinitionResponse,
  DeleteTagResponse,
  DenialData,
  DenyUserResponse,
  Donation,
  DonorMatchStatus,
  DonorNeighborhoodMapResponse,
  FriendInput,
  GetActivityLogsParams,
  GetActivityLogsResponse,
  GetAddressesFilter,
  GetAddressesResponse,
  GetAdminAssignmentsResponse,
  GetAssignmentsResponse,
  GetContactHistoryResponse,
  GetDonationsResponse,
  GetJobStatusResponse,
  GetPeopleByAssignmentResponse,
  GetPersonAssignmentsResponse,
  GetPersonDonationsResponse,
  GetPreApprovalsResponse,
  GetTagApplicationsResponse,
  GetTagDefinitionsResponse,
  GetUnsearchedAddressesResponse,
  ImportFriendsResponse,
  ImportVotedListResponse,
  LinkDonationData,
  LinkDonationResponse,
  ListJobsResponse,
  MarkAddressAsSearchedResponse,
  MarkDonorPostcardResponse,
  Note,
  PendingUser,
  PersonDetail,
  PostcardCampaign,
  PostcardRecipient,
  PreApproval,
  RemovePersonAssignmentResponse,
  Tag,
  UnbanUserResponse,
  UnlinkDonationResponse,
  UpdateAssignmentData,
  UpdateAssignmentResponse,
  UpdateDonationData,
  UpdateLinkData,
  UpdateLinkResponse,
  UpdatePersonAssignmentData,
  UpdatePersonAssignmentResponse,
  UpdatePhoneData,
  UpdatePhoneResponse,
  UpdatePostcardCampaignData,
  UpdatePreferredNameData,
  UpdatePreferredNameResponse,
  UpdateTagDefinitionData,
  UpdateTagDefinitionResponse,
  UpdateUserData,
  UpdateUserResponse,
  User,
  UserAssignment,
  UserWithDetails,
  VotedListRecord,
} from "@campaign/shared-types";
import { fetchAuthSession } from "aws-amplify/auth";

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * Get Authorization header with JWT token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (!token) {
      throw new Error("No authentication token found");
    }

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw new Error("Authentication required");
  }
}

/**
 * Make authenticated REST API call
 */
async function apiCall<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `API Error: ${response.status}`);
  }

  return response.json();
}

// ============================================
// API FUNCTIONS
// ============================================

export async function getMe(): Promise<User> {
  return apiCall<User>("/me");
}

export async function getAddresses(
  filter?: GetAddressesFilter
): Promise<Address[]> {
  const allAddresses: Address[] = [];
  let nextToken: string | null = null;

  do {
    const queryParams = new URLSearchParams({
      limit: "9000",
    });

    if (filter) {
      queryParams.append("filter", JSON.stringify(filter));
    }

    if (nextToken) {
      queryParams.append("nextToken", nextToken);
    }

    const response = await apiCall<GetAddressesResponse>(
      `/addresses?${queryParams.toString()}`
    );

    allAddresses.push(...response.items);
    nextToken = response.nextToken;

    console.log(
      `Fetched ${response.items.length} addresses. Total so far: ${
        allAddresses.length
      }. Next token: ${nextToken ? "exists" : "null"}`
    );
  } while (nextToken);

  return allAddresses;
}

export async function getAddressDetail(
  addressId: string
): Promise<AddressDetail> {
  return apiCall<AddressDetail>(`/addresses/${addressId}`);
}

export async function getPersonDetail(stateId: string): Promise<PersonDetail> {
  return apiCall<PersonDetail>(`/people/${stateId}`);
}

export async function getDashboard(): Promise<DashboardMetrics> {
  return apiCall<DashboardMetrics>("/dashboard");
}

export async function addNote(input: {
  addressId: string;
  personStateId?: string;
  content: string;
  citationUrl?: string;
  adminOnly: boolean;
}): Promise<Note> {
  const noteData: CreateNoteData = {
    content: input.content,
    citationUrl: input.citationUrl,
    adminOnly: input.adminOnly,
    personStateId: input.personStateId,
  };
  return apiCall<Note>(`/addresses/${input.addressId}/notes`, {
    method: "POST",
    body: JSON.stringify(noteData),
  });
}

export async function addTag(input: {
  addressId: string;
  personStateId?: string;
  tagId: string;
}): Promise<Tag> {
  const tagData: CreateTagData = {
    tagId: input.tagId,
    personStateId: input.personStateId,
  };
  return apiCall<Tag>(`/addresses/${input.addressId}/tags`, {
    method: "POST",
    body: JSON.stringify(tagData),
  });
}

export async function removeTag(
  addressId: string,
  tagId: string,
  personStateId?: string
): Promise<DeleteTagResponse> {
  const queryParams = personStateId ? `?personStateId=${personStateId}` : "";
  return apiCall<DeleteTagResponse>(
    `/addresses/${addressId}/tags/${tagId}${queryParams}`,
    {
      method: "DELETE",
    }
  );
}

// Admin endpoints
export async function getUnsearchedAddresses(params?: {
  streetContains?: string;
  sortBy?: "voteCount" | "street";
}): Promise<GetUnsearchedAddressesResponse> {
  const qs = new URLSearchParams();
  if (params?.streetContains) qs.set("streetContains", params.streetContains);
  if (params?.sortBy) qs.set("sortBy", params.sortBy);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  return apiCall<GetUnsearchedAddressesResponse>(
    `/admin/unsearched-addresses${query}`
  );
}

export async function createLinkForPerson(
  stateId: string,
  linkData: CreateLinkData
): Promise<CreateLinkResponse> {
  return apiCall<CreateLinkResponse>(`/people/${stateId}/links`, {
    method: "POST",
    body: JSON.stringify(linkData),
  });
}

export async function updateLinkForPerson(
  stateId: string,
  linkId: string,
  linkData: UpdateLinkData
): Promise<UpdateLinkResponse> {
  const transformedLinkData = {
    ...linkData,
    assignment: linkData.assignments
      ? linkData.assignments.join(",")
      : undefined,
  };
  return apiCall<UpdateLinkResponse>(`/people/${stateId}/links/${linkId}`, {
    method: "PUT",
    body: JSON.stringify(transformedLinkData),
  });
}

export async function updateAddress(
  addressId: string,
  data: {
    neighborhood?: string;
    elementaryZone?: string;
    middleZone?: string;
    highZone?: string;
  }
): Promise<{ success: boolean }> {
  return apiCall<{ success: boolean }>(`/addresses/${addressId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function markAddressAsSearched(
  addressId: string
): Promise<MarkAddressAsSearchedResponse> {
  return apiCall<MarkAddressAsSearchedResponse>(
    `/addresses/${addressId}/mark-searched`,
    {
      method: "POST",
    }
  );
}

export async function updatePersonPreferredName(
  stateId: string,
  preferredNameData: UpdatePreferredNameData
): Promise<UpdatePreferredNameResponse> {
  return apiCall<UpdatePreferredNameResponse>(
    `/people/${stateId}/preferred-name`,
    {
      method: "POST",
      body: JSON.stringify(preferredNameData),
    }
  );
}

export async function updatePersonPhone(
  phoneData: UpdatePhoneData
): Promise<UpdatePhoneResponse> {
  return apiCall<UpdatePhoneResponse>(`/people/${phoneData.stateId}/phone`, {
    method: "POST",
    body: JSON.stringify(phoneData),
  });
}

export async function addContact(
  stateId: string,
  contactData: CreateContactData
): Promise<Contact> {
  return apiCall<Contact>(`/people/${stateId}/contacts`, {
    method: "POST",
    body: JSON.stringify(contactData),
  });
}

export async function getContactHistory(
  stateId: string
): Promise<GetContactHistoryResponse> {
  return apiCall<GetContactHistoryResponse>(`/people/${stateId}/contacts`);
}

export async function deleteContact(
  stateId: string,
  contactId: string
): Promise<void> {
  await apiCall<{ success: boolean }>(
    `/people/${stateId}/contacts/${contactId}`,
    { method: "DELETE" }
  );
}

export async function getAssignments(): Promise<GetAssignmentsResponse> {
  return apiCall<GetAssignmentsResponse>("/assignments");
}

export async function getPeopleByAssignment(
  assignmentName: string
): Promise<GetPeopleByAssignmentResponse> {
  const encodedName = encodeURIComponent(assignmentName);
  return apiCall<GetPeopleByAssignmentResponse>(
    `/assignments/${encodedName}/people`
  );
}

export async function getPersonAssignments(
  stateId: string
): Promise<GetPersonAssignmentsResponse> {
  return apiCall<GetPersonAssignmentsResponse>(
    `/people/${stateId}/assignments`
  );
}

export async function addPersonAssignment(
  stateId: string,
  assignmentData: AddPersonAssignmentData
): Promise<AddPersonAssignmentResponse> {
  return apiCall<AddPersonAssignmentResponse>(
    `/people/${stateId}/assignments`,
    {
      method: "POST",
      body: JSON.stringify(assignmentData),
    }
  );
}

export async function removePersonAssignment(
  stateId: string,
  assignmentName: string
): Promise<RemovePersonAssignmentResponse> {
  const encodedName = encodeURIComponent(assignmentName);
  return apiCall<RemovePersonAssignmentResponse>(
    `/people/${stateId}/assignments/${encodedName}`,
    {
      method: "DELETE",
    }
  );
}

export async function updatePersonAssignment(
  stateId: string,
  assignmentName: string,
  updateData: UpdatePersonAssignmentData
): Promise<UpdatePersonAssignmentResponse> {
  const encodedName = encodeURIComponent(assignmentName);
  return apiCall<UpdatePersonAssignmentResponse>(
    `/people/${stateId}/assignments/${encodedName}`,
    {
      method: "PATCH",
      body: JSON.stringify(updateData),
    }
  );
}

// ============================================
// ADMIN API FUNCTIONS
// ============================================

export async function getPendingUsers(): Promise<{ users: PendingUser[] }> {
  return apiCall<{ users: PendingUser[] }>("/admin/pending-users");
}

export async function approveUser(
  email: string,
  data: ApprovalData
): Promise<ApproveUserResponse> {
  const encodedEmail = encodeURIComponent(email);
  return apiCall<ApproveUserResponse>(
    `/admin/pending-users/${encodedEmail}/approve`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function denyUser(
  email: string,
  data: DenialData
): Promise<DenyUserResponse> {
  const encodedEmail = encodeURIComponent(email);
  return apiCall<DenyUserResponse>(
    `/admin/pending-users/${encodedEmail}/deny`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function getAllUsers(): Promise<{ users: UserWithDetails[] }> {
  return apiCall<{ users: UserWithDetails[] }>("/admin/users");
}

export async function updateUser(
  email: string,
  data: UpdateUserData
): Promise<UpdateUserResponse> {
  const encodedEmail = encodeURIComponent(email);
  return apiCall<UpdateUserResponse>(`/admin/users/${encodedEmail}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function banUser(
  email: string,
  data: BanUserData
): Promise<BanUserResponse> {
  const encodedEmail = encodeURIComponent(email);
  return apiCall<BanUserResponse>(`/admin/users/${encodedEmail}/ban`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function unbanUser(email: string): Promise<UnbanUserResponse> {
  const encodedEmail = encodeURIComponent(email);
  return apiCall<UnbanUserResponse>(`/admin/users/${encodedEmail}/unban`, {
    method: "POST",
  });
}

/**
 * Admin Assignment Management
 */

export async function getAdminAssignments(): Promise<GetAdminAssignmentsResponse> {
  return apiCall<GetAdminAssignmentsResponse>("/admin/assignments");
}

export async function createAssignment(
  data: CreateAssignmentData
): Promise<CreateAssignmentResponse> {
  return apiCall<CreateAssignmentResponse>("/admin/assignments", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAssignment(
  assignmentName: string,
  data: UpdateAssignmentData
): Promise<UpdateAssignmentResponse> {
  const encodedName = encodeURIComponent(assignmentName);
  return apiCall<UpdateAssignmentResponse>(
    `/admin/assignments/${encodedName}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

export async function deleteAssignment(
  assignmentName: string,
  force: boolean = false
): Promise<DeleteAssignmentResponse> {
  const encodedName = encodeURIComponent(assignmentName);
  const queryParam = force ? "?force=true" : "";
  return apiCall<DeleteAssignmentResponse>(
    `/admin/assignments/${encodedName}${queryParam}`,
    {
      method: "DELETE",
    }
  );
}

/**
 * Cut Turf API Methods
 */

export interface AddressWithPeople extends Address {
  people?: Array<{
    stateId: string;
    name: string;
    age: number | null;
    sex: string | null;
    votingHistory?: Record<string, string>;
    assignments?: UserAssignment[];
  }>;
}

/**
 * Get addresses with full people details (for cut turf filtering)
 */
export async function getAddressesWithPeople(): Promise<AddressWithPeople[]> {
  const allAddresses: AddressWithPeople[] = [];
  let nextToken: string | null = null;

  do {
    const queryParams = new URLSearchParams({
      include: "people",
      limit: "9000",
    });

    if (nextToken) {
      queryParams.append("nextToken", nextToken);
    }

    const response = await apiCall<{
      items: AddressWithPeople[];
      totalCount: number;
      nextToken: string | null;
    }>(`/addresses?${queryParams.toString()}`);

    allAddresses.push(...response.items);
    nextToken = response.nextToken;

    console.log(
      `Fetched ${response.items.length} addresses with people. Total so far: ${
        allAddresses.length
      }. Next token: ${nextToken ? "exists" : "null"}`
    );
  } while (nextToken);

  return allAddresses;
}

/**
 * Batch assign people to a turf assignment
 */
export async function batchAssignToTurf(
  assignmentName: string,
  data: BatchAssignPeopleData
): Promise<BatchAssignPeopleResponse> {
  return apiCall<BatchAssignPeopleResponse>(
    `/admin/assignments/${encodeURIComponent(assignmentName)}/batch-assign`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

/**
 * Export turf list to CSV
 */
export function exportTurfToCsv(
  people: Array<{
    stateId: string;
    name: string;
    age: number | null;
    sex: string | null;
    address: Address;
  }>,
  assignmentName: string
): void {
  const headers = [
    "State ID",
    "Name",
    "Age",
    "Sex",
    "Address",
    "City",
    "Neighborhood",
  ];
  const rows = people.map((p) => [
    p.stateId,
    p.name,
    p.age?.toString() || "",
    p.sex || "",
    p.address.street,
    p.address.city,
    p.address.neighborhood || "",
  ]);

  const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `turf-${assignmentName}-${
    new Date().toISOString().split("T")[0]
  }.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Friend Matching API Methods
 */

/**
 * Import Facebook friends (async job)
 * Returns immediately with a jobId that can be polled for results
 */
export async function importFriends(
  friends: Array<{ name: string; url: string }>
): Promise<ImportFriendsResponse> {
  // Map frontend format to backend format (url -> fbProfileUrl)
  const friendInputs: FriendInput[] = friends.map((f) => ({
    name: f.name,
    fbProfileUrl: f.url,
  }));

  return apiCall<ImportFriendsResponse>("/friends/import", {
    method: "POST",
    body: JSON.stringify({ friends: friendInputs }),
  });
}

/**
 * Get friend import job status and results
 */
export async function getJobStatus(
  jobId: string
): Promise<GetJobStatusResponse> {
  return apiCall<GetJobStatusResponse>(`/friends/jobs/${jobId}`);
}

/**
 * List all friend import jobs for current user
 */
export async function listJobs(): Promise<ListJobsResponse> {
  return apiCall<ListJobsResponse>("/friends/jobs");
}

export async function assignFriendMatch(
  matchId: string,
  notes?: string
): Promise<AssignFriendMatchResponse> {
  return apiCall<AssignFriendMatchResponse>(
    `/friends/matches/${matchId}/assign`,
    {
      method: "POST",
      body: JSON.stringify({ notes }),
    }
  );
}

/**
 * Get structured assignment detail for two-phase contact strategy
 * Returns people categorized by: Priority (not voted), Already Voted, Not Interested
 */
export async function getAssignmentDetail(
  assignmentName: string
): Promise<AssignmentDetailResponse> {
  return apiCall<AssignmentDetailResponse>(
    `/assignments/${encodeURIComponent(assignmentName)}`
  );
}

/**
 * Admin: Import CSV of people who voted during early voting
 */
export async function importVotedList(
  votedData: VotedListRecord[]
): Promise<ImportVotedListResponse> {
  return apiCall<ImportVotedListResponse>("/admin/import-voted", {
    method: "POST",
    body: JSON.stringify({ votedData }),
  });
}

// ============================================
// PRE-APPROVAL API FUNCTIONS
// ============================================

/**
 * Get all pre-approvals (used and unused)
 */
export async function getPreApprovals(): Promise<GetPreApprovalsResponse> {
  return apiCall<GetPreApprovalsResponse>("/admin/pre-approvals");
}

/**
 * Create a new pre-approval
 */
export async function createPreApproval(
  data: CreatePreApprovalData
): Promise<PreApproval> {
  return apiCall<PreApproval>("/admin/pre-approvals", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * Delete an unused pre-approval
 */
export async function deletePreApproval(
  email: string
): Promise<DeletePreApprovalResponse> {
  // URL encode the email to handle special characters
  const encodedEmail = encodeURIComponent(email);
  return apiCall<DeletePreApprovalResponse>(
    `/admin/pre-approvals/${encodedEmail}`,
    {
      method: "DELETE",
    }
  );
}

// ============================================
// TAG MANAGEMENT API FUNCTIONS
// ============================================

/**
 * Get all tag definitions with usage counts
 */
export async function getTagDefinitions(
  includeArchived: boolean = false
): Promise<GetTagDefinitionsResponse> {
  const query = includeArchived ? "?includeArchived=true" : "";
  return apiCall<GetTagDefinitionsResponse>(`/admin/tags${query}`);
}

/**
 * Create a new tag definition
 */
export async function createTagDefinition(
  data: CreateTagDefinitionData
): Promise<CreateTagDefinitionResponse> {
  return apiCall<CreateTagDefinitionResponse>("/admin/tags", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing tag definition
 */
export async function updateTagDefinition(
  tagId: string,
  data: UpdateTagDefinitionData
): Promise<UpdateTagDefinitionResponse> {
  const encodedTagId = encodeURIComponent(tagId);
  return apiCall<UpdateTagDefinitionResponse>(`/admin/tags/${encodedTagId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/**
 * Delete a tag definition
 */
export async function deleteTagDefinition(
  tagId: string,
  force: boolean = false
): Promise<DeleteTagDefinitionResponse> {
  const encodedTagId = encodeURIComponent(tagId);
  const queryParams = force ? "?force=true" : "";
  return apiCall<DeleteTagDefinitionResponse>(
    `/admin/tags/${encodedTagId}${queryParams}`,
    {
      method: "DELETE",
    }
  );
}

/**
 * Get all applications of a specific tag (reverse lookup)
 */
export async function getTagApplications(
  tagId: string
): Promise<GetTagApplicationsResponse> {
  const encodedTagId = encodeURIComponent(tagId);
  return apiCall<GetTagApplicationsResponse>(
    `/admin/tags/${encodedTagId}/applications`
  );
}

// ============================================
// ACTIVITY LOG API FUNCTIONS
// ============================================

/**
 * Get activity log entries (admin only)
 */
export async function getActivityLogs(
  params: GetActivityLogsParams
): Promise<GetActivityLogsResponse> {
  const query = new URLSearchParams();
  if (params.userEmail) query.set("userEmail", params.userEmail);
  if (params.action) query.set("action", params.action);
  if (params.startDate) query.set("startDate", params.startDate);
  if (params.endDate) query.set("endDate", params.endDate);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.nextToken) query.set("nextToken", params.nextToken);

  return apiCall<GetActivityLogsResponse>(
    `/admin/activity-logs?${query.toString()}`
  );
}

// ============================================
// POSTCARD CAMPAIGN API FUNCTIONS
// ============================================

export async function getAdminPostcardCampaigns(): Promise<PostcardCampaign[]> {
  return apiCall<PostcardCampaign[]>("/admin/postcard-campaigns");
}

export async function createPostcardCampaign(
  data: CreatePostcardCampaignData
): Promise<PostcardCampaign> {
  return apiCall<PostcardCampaign>("/admin/postcard-campaigns", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePostcardCampaign(
  campaignId: string,
  data: UpdatePostcardCampaignData
): Promise<PostcardCampaign> {
  return apiCall<PostcardCampaign>(`/admin/postcard-campaigns/${campaignId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deletePostcardCampaign(
  campaignId: string
): Promise<void> {
  await apiCall<{ success: boolean }>(
    `/admin/postcard-campaigns/${campaignId}`,
    { method: "DELETE" }
  );
}

export async function getActivePostcardCampaigns(): Promise<
  PostcardCampaign[]
> {
  return apiCall<PostcardCampaign[]>("/postcard-campaigns");
}

export async function getPostcardRecipients(
  campaignId: string
): Promise<PostcardRecipient[]> {
  return apiCall<PostcardRecipient[]>(
    `/postcard-campaigns/${campaignId}/recipients`
  );
}

export async function deprioritizePostcardRecipient(
  campaignId: string,
  recipientKey: string
): Promise<void> {
  await apiCall<void>(
    `/postcard-campaigns/${campaignId}/recipients/${recipientKey}/deprioritize`,
    { method: "POST" }
  );
}

export async function claimPostcardCampaign(
  campaignId: string
): Promise<PostcardCampaign> {
  return apiCall<PostcardCampaign>(`/postcard-campaigns/${campaignId}/claim`, {
    method: "POST",
  });
}

export async function unclaimPostcardCampaign(
  campaignId: string
): Promise<PostcardCampaign> {
  return apiCall<PostcardCampaign>(`/postcard-campaigns/${campaignId}/claim`, {
    method: "DELETE",
  });
}

// ─── Donor / Donation API ────────────────────────────────────────────────────

export async function getDonations(
  status?: DonorMatchStatus
): Promise<Donation[]> {
  const qs = status ? `?status=${status}` : "";
  const res = await apiCall<Donation[] | GetDonationsResponse>(
    `/admin/donors${qs}`
  );
  return Array.isArray(res) ? res : (res as GetDonationsResponse).donations;
}

export async function getDonorNeighborhoodMap(
  minCount?: number
): Promise<DonorNeighborhoodMapResponse> {
  const qs = minCount !== undefined ? `?minCount=${minCount}` : "";
  return apiCall<DonorNeighborhoodMapResponse>(`/admin/donors/map${qs}`);
}

export async function getDonation(donationId: string): Promise<Donation> {
  return apiCall<Donation>(`/admin/donors/${donationId}`);
}

export async function updateDonation(
  donationId: string,
  data: UpdateDonationData
): Promise<Donation> {
  return apiCall<Donation>(`/admin/donors/${donationId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function linkDonation(
  donationId: string,
  data: LinkDonationData
): Promise<LinkDonationResponse> {
  return apiCall<LinkDonationResponse>(`/admin/donors/${donationId}/link`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function unlinkDonation(
  donationId: string
): Promise<UnlinkDonationResponse> {
  return apiCall<UnlinkDonationResponse>(`/admin/donors/${donationId}/link`, {
    method: "DELETE",
  });
}

export async function markDonorPostcard(
  donationId: string
): Promise<MarkDonorPostcardResponse> {
  return apiCall<MarkDonorPostcardResponse>(
    `/admin/donors/${donationId}/postcard`,
    { method: "POST" }
  );
}

export async function getPersonDonations(stateId: string): Promise<Donation[]> {
  const res = await apiCall<Donation[] | GetPersonDonationsResponse>(
    `/people/${stateId}/donations`
  );
  return Array.isArray(res)
    ? res
    : (res as GetPersonDonationsResponse).donations;
}
